import { cn } from '@/lib/utils';
import type { ReactElement, ReactNode } from 'react';

/** Labeled segmented control — a leading caps label + a row of
 *  mutually-exclusive toggle buttons. Shared by the sticky filter bars
 *  on the Audits and Field-data tabs so the two read identically. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  onChange: (next: T) => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </span>
      <div
        // biome-ignore lint/a11y/useSemanticElements: segmented control is a button row + role=group
        role="group"
        aria-label={label}
        className="inline-flex items-center gap-1 text-xs"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                'inline-flex items-center gap-1 rounded px-1 py-1 outline-none transition-colors',
                active
                  ? 'font-semibold text-[var(--color-ink)]'
                  : 'text-[var(--color-mute)] hover:text-[var(--color-ink)]',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
